import { useEffect, useRef, useState } from 'react';
import { Badge, Banner, BlockStack, Box, Button, Card, Divider, Icon, InlineStack, Link, SkeletonBodyText, Text } from '@shopify/polaris';
import { TypedFlight_radar, useDeleteFlightRadar, useGetInfiniteFlightRadars } from 'queries/flight_radar.query';
import dayjs from '../utils/dayjs';
import { AirplaneIcon, ClockIcon, DeleteIcon, LocationIcon } from '@shopify/polaris-icons';
import { useNotification } from 'context/NotificationContext';
import { formatFlightStatus, getFlightStatusType } from '../utils/flightStatus';

function flightStatus(flight: TypedFlight_radar) {
  const label = formatFlightStatus(flight.status);

  switch (getFlightStatusType(flight.status)) {
    case 'landed':
      return { label, tone: 'success' as const };
    case 'en route':
      return { label, tone: 'info' as const };
    case 'cancelled':
      return { label, tone: 'critical' as const };
    case 'delayed':
      return { label, tone: 'warning' as const };
    case 'scheduled':
      return { label, tone: 'attention' as const };
    default:
      return { label, tone: undefined };
  }
}

function formatFlightDate(value?: string, dateOnly = false) {
  if (!value) return '—';
  const parsed = /^\d+$/.test(value) ? dayjs(Number(value)) : dayjs(value);
  return parsed.isValid() ? parsed.format(dateOnly ? 'DD/MM/YYYY' : 'HH:mm DD/MM/YYYY') : '—';
}

export default function FlightRadar() {
  const { addNotification } = useNotification();
  const { mutateAsync: deleteFlightRadar, isPending: deleting, variables: deletingId } = useDeleteFlightRadar();
  const deleteInProgress = useRef(false);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | bigint | null>(null);

  const handleDelete = async (flight: TypedFlight_radar) => {
    if (!flight.id || deleteInProgress.current) return;

    // Lần 1: hỏi xác nhận
    if (confirmDeleteId !== flight.id) {
      setConfirmDeleteId(flight.id);
      return;
    }

    deleteInProgress.current = true;
    try {
      await deleteFlightRadar(flight.id);
      addNotification('info', 'Đã xóa theo dõi chuyến bay.');
    } catch (error: any) {
      addNotification('error', error?.response?.data?.message || error?.message || 'Không thể xóa theo dõi chuyến bay.');
    } finally {
      deleteInProgress.current = false;
    }
  };

  const endRef = useRef<HTMLDivElement>(null);
  const { data, isLoading, isFetching, isError, hasNextPage, fetchNextPage } = useGetInfiniteFlightRadars({ limit: 20, sort: 'createdAt:desc' });
  const flights = data?.pages.flatMap((page) => page.body) || [];
  const totalItems = data?.pages[0]?.totalItems;

  useEffect(() => {
    const target = endRef.current;
    if (!target || !hasNextPage || isFetching || isError) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) void fetchNextPage();
    });
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasNextPage, isFetching, isError, fetchNextPage]);

  return (
    <Box padding="400">
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center" gap="200">
          <Text as="p" tone="subdued">
            {totalItems != null ? `${totalItems} chuyến bay được theo dõi` : 'Danh sách chuyến bay'}
          </Text>
        </InlineStack>
        {isError && <Banner tone="critical">Không tải được danh sách chuyến bay. Vui lòng đóng và mở lại cửa sổ để thử lại.</Banner>}
        {isLoading && <SkeletonBodyText lines={5} />}

        {flights.map((flight) => {
          const status = flightStatus(flight);
          const orders = flight.flight_radar_to_order || [];
          return (
            <Card key={flight.id || `${flight.flight}-${flight.departure_date}`}>
              <BlockStack gap="300">
                <InlineStack align="space-between" blockAlign="center" gap="200">
                  <InlineStack gap="200" blockAlign="center" wrap={false}>
                    <Box background="bg-fill-info-secondary" borderRadius="200" padding="200">
                      <Icon source={AirplaneIcon} tone="info" />
                    </Box>
                    <BlockStack gap="050">
                      <Text as="h3" variant="headingLg">
                        {flight.flight || 'Chưa có mã chuyến bay'}
                      </Text>
                      <Text as="p" tone="subdued">
                        {formatFlightDate(flight.departure_date, true)}
                      </Text>
                    </BlockStack>
                  </InlineStack>
                  <InlineStack gap="200" blockAlign="center">
                    <Badge tone={status.tone}>{status.label}</Badge>
                    <Button
                      variant={confirmDeleteId === flight.id ? 'plain' : 'plain'}
                      tone="critical"
                      loading={deleting && deletingId === flight.id}
                      onClick={() => void handleDelete(flight)}
                      icon={DeleteIcon}
                    >
                      {confirmDeleteId === flight.id ? 'Chắc chắn?' : undefined}
                    </Button>
                  </InlineStack>
                </InlineStack>

                <Box background="bg-surface-secondary" borderRadius="300" padding="300">
                  <InlineStack align="space-between" blockAlign="center" gap="400" wrap>
                    <InlineStack gap="200" blockAlign="center" wrap={false}>
                      <Icon source={LocationIcon} tone="subdued" />
                      <BlockStack gap="050">
                        <Text as="p" tone="subdued" variant="bodySm">
                          Nơi bay
                        </Text>
                        <Text as="p" fontWeight="semibold">
                          {flight.flight_from || 'Chưa xác định'}
                        </Text>
                      </BlockStack>
                    </InlineStack>
                    <Text as="span" tone="subdued">
                      →
                    </Text>
                    <InlineStack gap="200" blockAlign="center" wrap={false}>
                      <Icon source={ClockIcon} tone="success" />
                      <BlockStack gap="050">
                        <Text as="p" tone="subdued" variant="bodySm">
                          Hạ cánh dự kiến
                        </Text>
                        <Text as="p" fontWeight="semibold">
                          {formatFlightDate(flight.estimated_landing_time || flight.scheduled_landing_time)}
                        </Text>
                      </BlockStack>
                    </InlineStack>
                  </InlineStack>
                </Box>

                <InlineStack gap="400" wrap>
                  <Text as="p" tone="subdued">
                    Nhà ga: <strong>{flight.terminal || '—'}</strong>
                  </Text>
                  <Text as="p" tone="subdued">
                    Lịch hạ cánh: <strong>{formatFlightDate(flight.scheduled_landing_time)}</strong>
                  </Text>
                  <Text as="p" tone="subdued">
                    Cập nhật: <strong>{formatFlightDate(flight.updatedAt)}</strong>
                  </Text>
                </InlineStack>
                <Divider />
                <BlockStack gap="200">
                  <Text as="p" fontWeight="semibold">
                    Đơn hàng theo dõi
                  </Text>
                  <InlineStack gap="200" wrap>
                    {orders.length === 0 && (
                      <Text as="p" tone="subdued">
                        Chưa có đơn hàng liên kết.
                      </Text>
                    )}
                    {orders.map((relation, index) => {
                      const orderId = relation.order_id || relation.orders?.id;
                      const orderCode = relation.orders?.order_code;
                      return orderId ? (
                        <Link key={relation.id || `${orderId}-${index}`} url={`/orders/view/${orderId}`}>
                          {orderCode || `Đơn #${orderId}`}
                        </Link>
                      ) : (
                        <Text key={relation.id || index} as="span" tone="subdued">
                          {orderCode || 'Chưa có mã đơn hàng'}
                        </Text>
                      );
                    })}
                  </InlineStack>
                </BlockStack>
              </BlockStack>
            </Card>
          );
        })}
        <div ref={endRef} aria-live="polite" style={{ minHeight: 1 }}>
          {isFetching && !isLoading && (
            <Text as="p" tone="subdued">
              Đang tải thêm chuyến bay...
            </Text>
          )}
          {!isLoading && !isError && !hasNextPage && (
            <Text as="p" tone="subdued" alignment="center">
              Không còn chuyến nào nữa
            </Text>
          )}
        </div>
      </BlockStack>
    </Box>
  );
}
